// FILE: src/pages/ForgotPasswordPage.jsx
// Ask for a reset link. The answer is the same whether or not the email has
// an account, so nobody can use this page to find out who is enrolled.
import React, { useState } from "react";
import { Alert, Box, Button, Card, CardContent, CircularProgress, Link, Stack, TextField, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { apiFetch } from "../services/api";
import PublicLayout from "../layouts/PublicLayout";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiFetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
    } catch {
      /* same message either way */
    } finally {
      setSubmitting(false);
      setSubmitted(true);
    }
  };

  return (
    <PublicLayout>
      <Card sx={{ borderRadius: 4 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography variant="h1" sx={{ mb: 1 }}>
            Reset your password
          </Typography>
          {submitted ? (
            <>
              <Alert severity="success" sx={{ my: 2 }} role="status">
                If an account uses that email address, we've sent it a link to set a new password. The link works for
                1 hour.
              </Alert>
              <Typography sx={{ color: "text.secondary", mb: 2 }}>
                Can't find the email? Check your junk or spam folder, or contact student support.
              </Typography>
              <Link component={RouterLink} to="/login">
                Back to sign in
              </Link>
            </>
          ) : (
            <>
              <Typography sx={{ color: "text.secondary", mb: 3 }}>
                Enter the email address you sign in with. We'll email you a link to choose a new password.
              </Typography>
              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Stack spacing={2.5}>
                  <TextField
                    label="Email"
                    type="email"
                    autoComplete="email"
                    inputProps={{ inputMode: "email" }}
                    fullWidth
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <Button type="submit" variant="contained" size="large" fullWidth disabled={submitting || !email}>
                    {submitting ? <CircularProgress size={24} color="inherit" aria-label="Sending" /> : "Send reset link"}
                  </Button>
                </Stack>
              </Box>
              <Box sx={{ mt: 3 }}>
                <Link component={RouterLink} to="/login">
                  Back to sign in
                </Link>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </PublicLayout>
  );
}
